/**
 * Money primitives for Vulpari.
 *
 * Every monetary amount in the system is an integer number of minor units
 * (pence for GBP, cents for EUR, and so on). We never use floating point for
 * money. The `Pence` brand makes it hard to accidentally mix a raw number with
 * a money value, and the helpers below keep all arithmetic in integer space.
 *
 * Formatting to a human-readable currency string happens only at the display
 * edge (the web app), never inside the engine.
 */
/** Construct a Pence value, rejecting any non-integer input. */
export function pence(value) {
    if (!Number.isInteger(value)) {
        throw new RangeError(`Money must be an integer number of minor units, received ${value}`);
    }
    return value;
}
/** Zero pence. */
export const ZERO = 0;
export function addPence(a, b) {
    return (a + b);
}
export function subPence(a, b) {
    return (a - b);
}
/** Sum a list of pence values, returning zero for an empty list. */
export function sumPence(values) {
    let total = 0;
    for (const value of values) {
        total += value;
    }
    return total;
}
/**
 * Apply a proportion expressed in basis points (1 bp = 0.01%). Working in
 * basis points keeps percentage flows in integer space and deterministic.
 * The result is floored so we never hand out a fraction of a penny.
 */
export function applyBasisPoints(base, basisPoints) {
    if (!Number.isInteger(basisPoints)) {
        throw new RangeError(`Basis points must be an integer, received ${basisPoints}`);
    }
    return Math.floor((base * basisPoints) / 10_000);
}
/** Largest of two pence values. */
export function maxPence(a, b) {
    return (a > b ? a : b);
}
/** Smallest of two pence values. */
export function minPence(a, b) {
    return (a < b ? a : b);
}
/**
 * Convert an integer minor-unit amount from one currency to another.
 *
 * `rates` gives the value of one unit of each currency in the primary currency
 * (so the primary currency itself is 1). Converting C to D is therefore
 * `amount * rate[C] / rate[D]`, rounded to whole minor units so money stays
 * integer. When the currencies match, or a rate is missing, the amount is
 * returned unchanged; callers that must not silently treat a missing rate as
 * 1:1 should check `hasRate` first.
 */
export function convertMinor(amount, from, to, rates) {
    if (from === to) {
        return amount;
    }
    const fromRate = rates?.[from];
    const toRate = rates?.[to];
    if (fromRate === undefined || toRate === undefined || toRate === 0) {
        return amount;
    }
    return Math.round((amount * fromRate) / toRate);
}
/** Whether a conversion between two currencies is possible with these rates. */
export function hasRate(from, to, rates) {
    return from === to || (rates?.[from] !== undefined && rates?.[to] !== undefined);
}
//# sourceMappingURL=money.js.map